import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { X } from "@phosphor-icons/react";
import { useFocusTrap } from "@/lib/use-focus-trap";
import { cn } from "@/lib/cn";
import { IconButton } from "./IconButton";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Removes the panel chrome for image/video inspection while retaining modal semantics. */
  bare?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/** Modal dialog: focus trap, Esc to close, scrim click to close. DESIGN.md §9/§7. */
export function Dialog({ open, onClose, title, bare = false, children, footer, className }: DialogProps) {
  const ref = useFocusTrap<HTMLDivElement>(open, onClose);
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        className={cn(
          "relative z-10 w-full max-w-md rounded-lg border border-border bg-surface-2 p-6 shadow-[var(--shadow-popover)]",
          bare && "!border-0 !bg-transparent !p-0 !shadow-none",
          className,
        )}
      >
        <div className={cn(bare ? "absolute right-3 top-3 z-10" : "mb-4 flex items-center justify-between")}>
          <h2 id="dialog-title" className={cn("text-h4", bare && "sr-only")}>
            {title}
          </h2>
          <IconButton label="Close" icon={<X size={18} weight="regular" />} variant={bare ? "ghost" : undefined} onClick={onClose} />
        </div>
        {children}
        {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
